import { Zip, ZipDeflate, ZipPassThrough } from 'fflate';

/**
 * Writes a Song Pack archive out of stored assets.
 *
 * Streaming, not `zipSync`, and the reason is the music video. `zipSync` wants
 * every input as one `Uint8Array` and produces the whole output as another, so
 * exporting a song with a 200 MB MV would need 200 MB in, 200 MB out and a
 * contiguous allocation for each. Here the video is read from its `Blob` in
 * chunks, passes through the zipper unchanged, and is written into a `Blob` the
 * browser can spill to disk — so the JavaScript heap holds a chunk at a time
 * rather than the file.
 *
 * The video is **stored**, not deflated. An MP4 is already compressed; running
 * deflate over it burns CPU to make the archive very slightly larger. Everything
 * else is deflated, which is what both existing producers do.
 */

/**
 * How much finished archive may sit in the stream queue.
 *
 * Small enough that the heap cost is a rounding error against a 200 MB video,
 * large enough that the zipper is never stalled waiting for a few kilobytes.
 */
const HIGH_WATER_MARK_BYTES = 4 * 1024 * 1024;

export interface ArchiveEntry {
  /** Bare file name at the archive root. Never a path. */
  readonly name: string;
  readonly blob: Blob;
  /** False for payloads that are already compressed. */
  readonly compress: boolean;
}

/** How much of the output was held in memory at once, for the record. */
export interface WriteReport {
  readonly bytes: number;
  readonly entries: number;
  /** Largest single chunk that passed through the zipper. */
  readonly peakChunkBytes: number;
  readonly elapsedMs: number;
}

export interface WriteResult {
  readonly blob: Blob;
  readonly report: WriteReport;
}

export async function writeSongPackArchive(
  entries: readonly ArchiveEntry[],
): Promise<WriteResult> {
  const started = performance.now();
  let peakChunkBytes = 0;
  let bytes = 0;

  // `start` runs synchronously inside the `ReadableStream` constructor, so this
  // is assigned before anything below can reach it — hence the definite
  // assignment rather than a nullable that every use would have to guard.
  let controller!: ReadableStreamDefaultController<Uint8Array>;
  // Backpressure. Without it the zipper pushes as fast as it can read from disk
  // and the stream's queue becomes the entire archive: measured at 179 MB of
  // heap for a 227 MB pack, which is exactly the copy this design exists to
  // avoid. `pull` fires when the consumer has drained below the mark, and the
  // pump below waits for it.
  let drained: (() => void) | null = null;
  const output = new ReadableStream<Uint8Array>(
    {
      start: (value) => {
        controller = value;
      },
      pull: () => {
        drained?.();
        drained = null;
      },
    },
    // Counted in bytes, not chunks: the chunks vary by two orders of magnitude
    // between a 600-byte manifest and a 2 MiB slice of video.
    new ByteLengthQueuingStrategy({ highWaterMark: HIGH_WATER_MARK_BYTES }),
  );

  const waitForDrain = async (): Promise<void> => {
    if (controller.desiredSize === null || controller.desiredSize > 0) return;
    await new Promise<void>((resolve) => {
      drained = resolve;
    });
  };

  // A box rather than a bare `let`: the zipper reports failure from a callback,
  // and the compiler would otherwise narrow the variable to `never` at the
  // checks below, having seen no assignment on the straight-line path.
  const state: { failure: Error | null } = { failure: null };
  const zip = new Zip((error, chunk, final) => {
    if (error !== null) {
      state.failure = error;
      controller.error(error);
      return;
    }
    bytes += chunk.length;
    if (chunk.length > peakChunkBytes) peakChunkBytes = chunk.length;
    controller.enqueue(chunk);
    if (final) controller.close();
  });

  // Started before anything is pushed: `Response.blob()` drains the stream as it
  // fills, which is what keeps the queue from becoming the whole archive.
  const collected = new Response(output).blob();

  try {
    for (const entry of entries) {
      const file = entry.compress ? new ZipDeflate(entry.name) : new ZipPassThrough(entry.name);
      zip.add(file);
      await pump(entry.blob, file, waitForDrain);
      if (state.failure !== null) break;
    }
    zip.end();
  } catch (cause) {
    controller.error(cause);
    throw cause;
  }

  const blob = await collected;
  if (state.failure !== null) throw state.failure;
  return {
    blob,
    report: {
      bytes,
      entries: entries.length,
      peakChunkBytes,
      elapsedMs: Math.round(performance.now() - started),
    },
  };
}

/**
 * Feeds one asset through the zipper a chunk at a time.
 *
 * Reads one chunk ahead so the last push can carry `final` — fflate closes an
 * entry on that flag, not on the source stream ending — and waits on
 * `waitForDrain` between chunks so the output queue cannot outrun the consumer.
 */
async function pump(
  blob: Blob,
  file: ZipDeflate | ZipPassThrough,
  waitForDrain: () => Promise<void>,
): Promise<void> {
  const reader = blob.stream().getReader();
  let pending: Uint8Array | null = null;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (pending !== null) {
        file.push(pending, false);
        await waitForDrain();
      }
      pending = value;
    }
    file.push(pending ?? new Uint8Array(0), true);
    await waitForDrain();
  } finally {
    reader.releaseLock();
  }
}
